import type { LucideIcon } from 'lucide-react';
import { cn } from './lib/cn';

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  /** Uma linha do que a opcao faz - aparece abaixo do grupo, para a escolhida. */
  hint?: string;
  icon?: LucideIcon;
}

export interface SegmentedProps<T extends string> {
  options: readonly SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  disabled?: boolean;
  /**
   * Sem fundo, so o contorno. Para barras de filtro, onde o cinza do grupo
   * competia com o conteudo que ele filtra.
   */
  flat?: boolean;
  /** Nome do grupo para leitores de tela - vira tambem o `name` dos radios. */
  label: string;
  /** `lg` para abas de pagina, onde o grupo e navegacao e nao filtro. */
  size?: 'default' | 'lg';
  className?: string;
}

/**
 * Escolha unica entre poucas opcoes, todas a vista. Vale a troca pelo select
 * quando as opcoes cabem na linha: ler as tres e um passo, abrir a lista para
 * descobrir quais existem sao dois.
 *
 * Por baixo sao radios de verdade - e o que da navegacao por setas e leitura
 * correta sem reimplementar nada.
 */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  disabled,
  flat,
  label,
  size = 'default',
  className,
}: SegmentedProps<T>) {
  const escolhida = options.find((item) => item.value === value);
  const grupo = label.replace(/\s+/g, '-').toLowerCase();

  return (
    <div className={className}>
      <div
        role="radiogroup"
        aria-label={label}
        className={cn(
          'inline-flex flex-wrap gap-1 rounded-md',
          // No modo sem fundo o grupo tambem perde a moldura: so a opcao
          // escolhida fica contornada, e e ela que precisa se destacar.
          flat ? 'bg-transparent' : 'bg-muted-foreground/10 p-1 pb-0',
        )}
      >
        {options.map((option) => {
          const ativo = option.value === value;
          const Icone = option.icon;

          return (
            <label
              key={option.value}
              className={cn(
                'flex items-center gap-1.5 rounded-t transition-colors',
                size === 'lg' ? 'gap-2 px-4 py-2.5 text-base' : 'px-3.5 py-1.5 text-sm',
                'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary/30',
                disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
                flat ? 'border' : 'border-b-[3px] border-transparent',
                ativo
                  ? flat
                    ? 'border-border-strong font-medium text-foreground'
                    : 'border-primary bg-card font-medium text-foreground shadow-[0_1px_3px_rgb(0_0_0/0.12)]'
                  : cn('text-muted-foreground hover:text-foreground', flat && 'border-transparent'),
              )}
            >
              <input
                type="radio"
                className="sr-only"
                name={grupo}
                value={option.value}
                checked={ativo}
                disabled={disabled}
                onChange={() => onChange(option.value)}
              />
              {Icone ? <Icone className={size === 'lg' ? 'h-4 w-4' : 'h-3.5 w-3.5'} /> : null}
              {option.label}
            </label>
          );
        })}
      </div>

      {escolhida?.hint ? (
        <p className="mt-1.5 text-xs text-muted-foreground">{escolhida.hint}</p>
      ) : null}
    </div>
  );
}
